<script setup lang="ts">
import { computed, onMounted, ref, shallowRef, watch } from "vue";
import axios from "axios";
import { endpoint } from "@/remote/utils";
import { fetchUsers } from "@/remote/user";
import { fetchParkById } from "@/remote/park";
import { fetchIssueById } from "@/remote/issue";
import _ from "lodash";
import { objComparatorNumber } from "@/utils";
import { ChargingStation, DoMaintenance, Issue, Park, Technician } from "@/types/do_maintenance.ts";

const props = defineProps({
  doMaintId: Number,
});
const emit = defineEmits(['loaded']);

const doMaint = shallowRef<DoMaintenance>();
const technicians = shallowRef<Technician[]>([]);
const park = shallowRef<Park>();
const issue = shallowRef<Issue | null>(null);
const signatureUrl = ref("");


const formattedIssue = computed(() => {
  if (issue.value === null) return null;
  const { park: _a, ...newIssue } = issue.value;
  return {
    ...newIssue,
    priority: ["High", "Medium", "Low"][newIssue.priority],
    made_by: technicians.value.find((t) => t.id === newIssue.made_by)?.username ?? "unknown",
  };
});

const formattedCPDeltas = computed(() => {
  return doMaint.value?.cp_deltas.map((d) => {
    d['park_bnum'] = park.value?.chargingstations.find((cs) => cs.bnum === d.maintenance_data)?.park_bnum ?? -1;
    return d;
  }).sort((a, b) => objComparatorNumber(a, b, 'park_bnum')).map((d) => {
    return { ...d, park_bnum: d['park_bnum'] !== -1 ? d['park_bnum'] : 'unknown' };
  });
});

async function fetchDoMaint(doId: number) {
  const res = await axios.get(`${endpoint("internal/current/domaintenances/")}${doId}/`, {
    withCredentials: true
  });
  return res.data;
}

async function fetchSignatureUrl(sigId: number): Promise<string> {
  const res = await axios.get(`${endpoint("/internal/current/domaintenancesignatures/")}${sigId}/`, {
    withCredentials: true
  });
  return res.data["url"];
}

function getCSByBnum(bnum: number): ChargingStation | null {
  return park.value?.chargingstations.find((cs) => cs.bnum === bnum) ?? null;
}

watch(() => props.doMaintId, async (newValue) => {
  if (newValue === undefined) {
    console.error("Tried rendering with doMaintId undefined");
    return;
  }
  [
    [doMaint.value, park.value, signatureUrl.value],
    technicians.value,
  ] = await Promise.all([
    fetchDoMaint(newValue).then(async (dmData: DoMaintenance) => {
      const [
        park,
        sigUrl
      ] = await Promise.all([
        fetchParkById(dmData.park),
        fetchSignatureUrl(dmData.signature)
      ]);
      return [dmData, park, sigUrl];
    }),
    fetchUsers('?is_active=1&is_readonly=0&is_staff=1'),
  ]);
  if (doMaint.value?.curative !== null) issue.value = await fetchIssueById(doMaint.value?.curative.ticket);
  emit('loaded');
}, { immediate: true });

onMounted(async () => {

});
</script>

<template>
  <v-container v-if="doMaint !== undefined && park !== undefined">
    <v-row>
      <v-col>
        <h2 class="text-center">General</h2>
        <v-table density="comfortable">
          <tbody>
          <tr>
            <td>ID</td>
            <td>{{ doMaint.id }}</td>
          </tr>
          <tr>
            <td>Park Name</td>
            <td>{{ park.name }}</td>
          </tr>
          <tr>
            <td>Technician{{ doMaint.technicians.length > 1 && 's' }}</td>
            <td>{{
                doMaint.technicians.map((tId) => technicians.find((t) => t.id === tId)?.username ?? 'unknown').join(', ')
              }}
            </td>
          </tr>
          <tr>
            <td>Submit Time</td>
            <td>{{ new Date(doMaint.submit_time).toLocaleString('sv') }}</td>
          </tr>
          <tr>
            <td>Execution Time</td>
            <td>{{ new Date(doMaint.execution_time).toLocaleString('sv') }}</td>
          </tr>
          <tr>
            <td>Notes</td>
            <td>{{ doMaint.notes }}</td>
          </tr>
          <tr v-if="signatureUrl !== ''">
            <td colspan="2">
              <div>
                <p class="mt-3">Signature</p>
                <v-img class="border-lg mt-3" :src="signatureUrl"/>
              </div>
            </td>
          </tr>
          </tbody>
        </v-table>
      </v-col>
      <v-col v-if="doMaint.curative !== null">
        <h2 class="text-center">Curative</h2>
        <v-table density="comfortable">
          <tbody>
          <template v-if="formattedIssue !== null">
            <tr v-for="[key, val] in Object.entries(formattedIssue as Record<string, any>)">
              <td>Ticket {{ _.startCase(key) }}</td>
              <td>{{ val }}</td>
            </tr>
          </template>
          <tr>
            <td>Things Done</td>
            <td>{{ doMaint.curative.things_done }}</td>
          </tr>
          </tbody>
        </v-table>
      </v-col>
      <template v-if="doMaint.preventive !== null">
        <v-col>
          <h2 class="text-center">Preventive</h2>
          <v-table density="comfortable">
            <tbody>
            <tr v-for="[key, val] in Object.entries(doMaint.preventive).filter(([k, _val]) => k !== 'cps')">
              <td>{{ _.startCase(key) }}</td>
              <td>{{ val }}</td>
            </tr>
            </tbody>
          </v-table>
        </v-col>
        <v-col>
          <h2 class="text-center">Preventive ChargingStations</h2>
          <template v-for="thing in doMaint.preventive.cps">
            <h4 class="mt-2">CP {{ getCSByBnum(thing.bnum)?.park_bnum }} - Bnum {{ thing.bnum }}{{
                getCSByBnum(thing.bnum)?.qrcodeid !== null && ` - QR ${getCSByBnum(thing.bnum)?.qrcodeid}`
              }}</h4>
            <v-table density="comfortable">
              <tbody>
              <tr v-for="[key, val] in Object.entries(thing).filter(([k, v]) => k !== 'bnum' && v !== null)">
                <td>{{ _.startCase(key) }}</td>
                <td>{{ val }}</td>
              </tr>
              </tbody>
            </v-table>
          </template>
        </v-col>
        <v-col>
          <h2 class="text-center">Changes Done</h2>
          <v-table density="compact">
            <thead>
            <tr style="font-weight: bold">
              <td>Field Changed</td>
              <td>Old Value</td>
              <td>New Value</td>
            </tr>
            </thead>
            <tbody>
            <tr v-for="delta in doMaint.deltas">
              <td>{{ delta.field_name }}</td>
              <td>{{ delta.old_value }}</td>
              <td>{{ delta.new_value }}</td>
            </tr>
            </tbody>
          </v-table>
        </v-col>
        <v-col>
          <h2 class="text-center">Changes Done on ChargingStations</h2>
          <v-table density="compact">
            <thead>
            <tr style="font-weight: bold">
              <td>CP Number</td>
              <td>Field Changed</td>
              <td>Old Value</td>
              <td>New Value</td>
            </tr>
            </thead>
            <tbody>
            <tr v-for="cpdelta in formattedCPDeltas">
              <td>{{ cpdelta.park_bnum }}</td>
              <td>{{ cpdelta.field_name }}</td>
              <td>{{ cpdelta.old_value }}</td>
              <td>{{ cpdelta.new_value }}</td>
            </tr>
            </tbody>
          </v-table>
        </v-col>
      </template>
    </v-row>
    <v-row>
      <v-col>
        <v-expansion-panels>
          <v-expansion-panel
            title="Raw Data"
          >
            <v-expansion-panel-text>
              <p style="white-space: pre">{{ JSON.stringify(doMaint, null, 2) }}</p>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
td {
  text-wrap: nowrap;
}
</style>
